import {
  Box,
  Button,
  Flex,
  H4,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
  OusterProvider,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useMemo } from 'react';
import { MergerRecord } from 'src/components/adminDashboard/types';

import { formatDateLong } from '../utils';
import { ActiveMergerAction, MERGER_ACTION, STATUS_STYLE_MAP } from './const';

interface CompletedRequestsProps {
  data: MergerRecord[];
  onOpenMerger: (mergerId: number, mode: 'view' | 'edit') => void;
  activeMergerAction: ActiveMergerAction | null;
}

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<MergerRecord>>(
    MaterialTableWrapper
  );

const renderMergedOrgCountCell: MRT_ColumnDef<MergerRecord>['Cell'] = ({
  cell,
}) => (
  <Box
    as="span"
    color="gray.200"
    bgColor="gray.700"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    height="20px"
    px={2}
    rounded={4}
    fontSize={12}
    lineHeight={1}
  >
    {`${cell.getValue<number[]>().length} sources`}
  </Box>
);

const renderStatusCell: MRT_ColumnDef<MergerRecord>['Cell'] = ({ row }) => {
  const mergerRecord = row.original;
  const statusStyle = STATUS_STYLE_MAP[mergerRecord.status];

  return (
    <Flex alignItems="center" gap={2} fontSize={12}>
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        height="20px"
        minW="80px"
        px={2}
        rounded={4}
        lineHeight={1}
        bg={statusStyle.bg}
        color={statusStyle.color}
      >
        {statusStyle.label}
      </Box>
    </Flex>
  );
};

const RowActions = ({
  row,
  onOpenMerger,
  activeMergerAction,
}: {
  row: MRT_Row<MergerRecord>;
  onOpenMerger: (mergerId: number, mode: 'view' | 'edit') => void;
  activeMergerAction: ActiveMergerAction | null;
}) => {
  const mergerRecord: MergerRecord = row.original;
  const isDetailsLoading =
    activeMergerAction?.mergerId === mergerRecord.merger_id &&
    activeMergerAction.action === MERGER_ACTION.OPEN;

  return (
    <OusterProvider>
      <Flex gap={2} alignItems="center">
        <Button
          colorScheme="gray"
          size="sm"
          onClick={() => onOpenMerger(mergerRecord.merger_id, 'view')}
          disabled={isDetailsLoading}
          style={isDetailsLoading ? { opacity: 0.7 } : {}}
        >
          {isDetailsLoading ? 'Loading...' : 'View'}
        </Button>
      </Flex>
    </OusterProvider>
  );
};

export default function CompletedRequests({
  data,
  onOpenMerger,
  activeMergerAction,
}: CompletedRequestsProps) {
  const columns = useMemo<MRT_ColumnDef<MergerRecord>[]>(
    () => [
      {
        accessorKey: 'target_org_name',
        header: 'Survivor Org',
      },
      {
        accessorKey: 'source_orgs',
        header: 'Merged Org number',
        Cell: renderMergedOrgCountCell,
      },
      {
        accessorKey: 'preparer_name',
        header: 'Prepared By',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: renderStatusCell,
      },
      {
        accessorKey: 'created_at',
        header: 'Created Date',
        Cell: ({ cell }) => formatDateLong(cell.getValue<string>()),
      },
    ],
    []
  );

  const tableOptions: MRT_TableOptions<MergerRecord> = {
    columns,
    data,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: {
        right: ['mrt-row-actions'],
        left: ['merger_id', 'target_org_name'],
      },
      showGlobalFilter: true,
    },
    enableRowActions: true,
    positionGlobalFilter: 'right',
    enableSorting: true,
    enableToolbarInternalActions: false,
    layoutMode: 'grid',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 350,
        maxSize: 350,
        grow: false,
      },
    },
    getRowId: (originalRow: MergerRecord): string => {
      const mergerId: number = originalRow.merger_id;
      return String(mergerId);
    },
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        onOpenMerger={onOpenMerger}
        activeMergerAction={activeMergerAction}
      />
    ),
    muiTablePaperProps: {
      sx: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    muiTableContainerProps: {
      sx: {
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)',
      },
    },
    muiTableBodyRowProps: {
      hover: false,
    },
  };

  return (
    <Box position="relative">
      <H4
        color="gray.300"
        fontSize={14}
        position="absolute"
        left={0}
        top={4}
        zIndex={2}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Box
          borderRadius="full"
          w={2}
          h={2}
          bg="green.300"
          display="inline-block"
          aria-hidden="true"
        />
        <span style={{ lineHeight: 1 }}>
          Finished Merge Requests: {data.length}
        </span>
      </H4>
      <Box zIndex={1}>
        <ThemedMaterialTableWrapper {...tableOptions} />
      </Box>
    </Box>
  );
}
